import { useState } from 'react';
import { Card, Tag, Row, Col, Statistic } from 'antd';
import { DollarOutlined } from '@ant-design/icons';
import { trpc } from '../../lib/api';
import { ResponsiveTable, type ResponsiveColumnType } from '../../components/common/responsive';
import { PageHeader, PageContainer } from '../../components/common';

type CommissionRow = {
  id: string;
  periodStart: string;
  periodEnd: string;
  totalSales: string;
  commissionRate: string;
  commissionAmount: string;
  taxAmount: string;
  paymentStatus: 'pending' | 'prepared' | 'paid' | 'canceled' | string;
  paidAt: string | null;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const normalizeCommissionRow = (value: unknown): CommissionRow | null => {
  if (!isRecord(value) || typeof value.id !== 'string') {
    return null;
  }

  return {
    id: value.id,
    periodStart: typeof value.periodStart === 'string' ? value.periodStart : '',
    periodEnd: typeof value.periodEnd === 'string' ? value.periodEnd : '',
    totalSales: typeof value.totalSales === 'string' ? value.totalSales : '0',
    commissionRate: typeof value.commissionRate === 'string' ? value.commissionRate : '0',
    commissionAmount: typeof value.commissionAmount === 'string' ? value.commissionAmount : '0',
    taxAmount: typeof value.taxAmount === 'string' ? value.taxAmount : '0',
    paymentStatus: typeof value.paymentStatus === 'string' ? value.paymentStatus : 'pending',
    paidAt: typeof value.paidAt === 'string' ? value.paidAt : null,
  };
};

export function AgencyCommissionsPage() {
  const [page, setPage] = useState(1);

  // 手数料一覧取得
  const { data, isLoading } = trpc.agencyCommission.listCommissions.useQuery({
    page,
    limit: 20,
  });
  const commissions = (data?.commissions ?? []).flatMap((commission) => {
    const normalized = normalizeCommissionRow(commission);
    return normalized ? [normalized] : [];
  });

  // 統計情報取得
  const { data: stats } = trpc.agencyCommission.getStatistics.useQuery();

  const columns: ResponsiveColumnType<CommissionRow>[] = [
    {
      title: '期間',
      key: 'period',
      render: (_, record) => {
        const start = new Date(record.periodStart).toLocaleDateString('ja-JP', {
          year: 'numeric',
          month: '2-digit',
        });
        const end = new Date(record.periodEnd).toLocaleDateString('ja-JP', {
          year: 'numeric',
          month: '2-digit',
        });
        return start === end ? start : `${start} - ${end}`;
      },
      mobileRole: 'title',
      mobilePriority: 1,
    },
    {
      title: '総売上',
      dataIndex: 'totalSales',
      key: 'totalSales',
      render: (sales) => `¥${parseFloat(sales).toLocaleString()}`,
      align: 'right' as const,
      mobileRole: 'description',
      mobilePriority: 2,
    },
    {
      title: '手数料レート',
      dataIndex: 'commissionRate',
      key: 'commissionRate',
      render: (rate) => `${parseFloat(rate)}%`,
      align: 'center' as const,
      mobileRole: 'hidden',
    },
    {
      title: '手数料額',
      dataIndex: 'commissionAmount',
      key: 'commissionAmount',
      render: (amount) => (
        <strong style={{ fontSize: '16px' }}>
          ¥{parseFloat(amount).toLocaleString()}
        </strong>
      ),
      align: 'right' as const,
      mobileRole: 'description',
      mobilePriority: 3,
    },
    {
      title: '税額',
      dataIndex: 'taxAmount',
      key: 'taxAmount',
      render: (amount) => `¥${parseFloat(amount).toLocaleString()}`,
      align: 'right' as const,
      mobileRole: 'hidden',
    },
    {
      title: 'ステータス',
      dataIndex: 'paymentStatus',
      key: 'paymentStatus',
      render: (status: string) => {
        const statusMap: Record<string, { color: string; label: string }> = {
          pending: { color: 'orange', label: '未払い' },
          prepared: { color: 'blue', label: '準備完了' },
          paid: { color: 'green', label: '支払済み' },
          canceled: { color: 'red', label: 'キャンセル' },
        };
        const info = statusMap[status] || { color: 'default', label: status };
        return <Tag color={info.color}>{info.label}</Tag>;
      },
      mobileRole: 'tag',
      mobilePriority: 4,
    },
    {
      title: '支払日',
      dataIndex: 'paidAt',
      key: 'paidAt',
      render: (date) =>
        date ? new Date(date).toLocaleDateString('ja-JP') : '-',
      mobileRole: 'hidden',
    },
  ];

  return (
    <PageContainer>
      <PageHeader title="手数料・売上管理" />

      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="総手数料"
              value={stats?.totalCommission || 0}
              prefix={<DollarOutlined />}
              precision={0}
              suffix="円"
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="支払済み"
              value={stats?.paidCommission || 0}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#3f8600' }}
              precision={0}
              suffix="円"
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="未払い"
              value={stats?.pendingCommission || 0}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#cf1322' }}
              precision={0}
              suffix="円"
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <ResponsiveTable<CommissionRow>
          dataSource={commissions}
          columns={columns}
          loading={isLoading}
          rowKey="id"
          pagination={{
            current: page,
            pageSize: 20,
            total: data?.pagination.total || 0,
            onChange: setPage,
          }}
          mobileCardConfig={{
            titleField: 'periodStart',
          }}
        />
      </Card>
    </PageContainer>
  );
}
